/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { useRef } from "react";
import { Modal } from "components/generals/Modal";
import Input from "components/generals/Input";
import Button from "components/generals/Button";
import Typography from "components/generals/Typography";
import Box from "components/generals/Box";
import ErrorIcon from "@mui/icons-material/Error";
import { useTranslation } from "react-i18next";
import Grid from "components/generals/Grid";

/**
 * Properties for the VerificationModal component.
 * 
 * @interface VerificationModalProps
 * @property {boolean} open - Whether modal is open/visible
 * @property {Function} onClose - Callback when modal is closed
 * @property {Function} onVerify - Callback when verification code is submitted
 * @property {Function} onResend - Callback to resend verification code
 * @property {boolean} codeError - Whether verification code is incorrect
 * @property {number} resendTimer - Seconds remaining until resend is available
 */
interface VerificationModalProps {
  open: boolean;
  onClose: () => void;
  onVerify: (code: string) => void;
  onResend: () => void;
  codeError: boolean;
  resendTimer: number;
}

/**
 * Two-factor authentication verification modal component.
 * Displays a 6-digit code input interface for security verification.
 * 
 * Features:
 * - Six individual input boxes for verification code digits
 * - Numeric input only (automatically filtered)
 * - Auto-focus on next input when digit entered
 * - Backspace handling: clears current digit or moves to previous
 * - Error display with icon when code is incorrect
 * - Resend code button with countdown timer
 * - Countdown display in MM:SS format
 * - Disabled resend during countdown (shows time remaining)
 * - Verify and Close action buttons
 * - Centered modal overlay
 * - i18n translation support
 * 
 * Input Behavior:
 * - Max 1 character per input
 * - Only numeric characters allowed (0-9)
 * - Automatic focus advance on digit entry
 * - Backspace: clear current or focus previous
 * 
 * Timer Behavior:
 * - Shows countdown when resendTimer > 0
 * - Format: "M:SS" (e.g., "2:30" for 150 seconds)
 * - Shows "Resend Code" button when timer reaches 0
 * - Displays expiration warning during countdown
 * 
 * Layout:
 * - Fixed width 320px centered box
 * - White background with shadow
 * - Rounded corners
 * - Title and instructions
 * - 6-digit input row
 * - Error message (conditional)
 * - Timer/resend area
 * - Verify (9 cols) and Close (3 cols) buttons
 * 
 * @param {VerificationModalProps} props - Component properties
 * @param {boolean} props.open - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onVerify - Verify handler (receives 6-digit code string)
 * @param {Function} props.onResend - Resend code handler
 * @param {boolean} props.codeError - Show error state
 * @param {number} props.resendTimer - Countdown seconds
 * 
 * @returns {JSX.Element} Two-factor authentication modal
 */
const VerificationModal = ({
  open,
  onClose,
  onVerify,
  onResend,
  codeError,
  resendTimer,
}: VerificationModalProps) => {
  const { t } = useTranslation();
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const codeContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Handles verification code submission.
   * Collects all 6 digits from input boxes and calls onVerify callback.
   */
  const handleVerify = () => {
    const code = Array.from(codeContainerRef.current?.querySelectorAll("input") || [])
      .map((input) => (input as HTMLInputElement).value)
      .join("");
    onVerify(code);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 320,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" component="h2">
          {t("login.twoFactorAuth")}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
          {t("login.enterSecurityCode")}
        </Typography>

        <Box ref={codeContainerRef} display="flex" justifyContent="center" gap={1}>
          {[...Array(6)].map((_, index) => (
            <Input
              key={index}
              type="text"
              inputMode="numeric"
              inputProps={{ maxLength: 1 }}
              inputRef={(el) => (inputRefs.current[index] = el)}
              onInput={(e) => {
                const input = e.target as HTMLInputElement;
                input.value = input.value.replace(/[^0-9]/g, "");
                if (input.value && inputRefs.current[index + 1]) {
                  inputRefs.current[index + 1]?.focus();
                }
              }}
              onKeyDown={(e) => {
                const input = e.currentTarget as HTMLInputElement;
                if (e.key === "Backspace") {
                  setTimeout(() => {
                    if (input.value) {
                      input.value = "";
                    } else if (inputRefs.current[index - 1]) {
                      inputRefs.current[index - 1]?.focus();
                    }
                  }, 0);
                }
              }}
            />
          ))}
        </Box>

        {codeError && (
          <Typography variant="caption" sx={{ color: "#f44336", mt: 1 }}>
            <ErrorIcon />
            {t("login.incorrectCode")}
          </Typography>
        )}

        <Box display="flex" flexDirection="column" justifyContent="space-between">
          <Box>
            {resendTimer > 0 ? (
              <Typography variant="caption" color="secondary">
                <strong>
                  {t("login.resendAvailableIn")} {Math.floor(resendTimer / 60)}:
                  {String(resendTimer % 60).padStart(2, "0")}
                </strong>
                <p>{t("login.expiredTime")}</p>
              </Typography>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Button variant="text" color="dark" fullWidth onClick={onResend}>
                  {t("login.resendCode")}
                </Button>
              </Box>
            )}
          </Box>
         <Grid container spacing={0.5} mt={1} alignItems="center">
            <Grid item xs={9}>
              <Button variant="gradient" color="info" fullWidth onClick={handleVerify}>
                {t("login.verify")}
              </Button>
            </Grid>
            <Grid item xs={3}>
              <Button variant="gradient" color="secondary" fullWidth onClick={onClose}>
                {t("login.close")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};

export default VerificationModal;
